import { TemplateResult, html, nothing } from 'lit';
import {
  FindingDetailsPayload,
  FindingGroup,
  OnClickDetailHandler,
  RelatedFinding,
} from '../findingDetails/types';
import { BaseWebComponentTranslator } from '../../../framework/BaseWebComponentTranslator';
import { FindingClassType, findingTypeToIconMap } from '../../types';
import { EASMIconSet } from '../../types/iconSet';

export class BaseSpecificDetailsView {
  protected static stringsTranslator = BaseWebComponentTranslator.getInstance();

  /**
   * Retrieves general information for the finding details.
   * @param findingDetails - The finding details payload.
   * @param dependencies - The dependencies.
   * @returns The general information template.
   */
  static getGeneralInfo(findingDetails: FindingDetailsPayload, dependencies: any): TemplateResult {
    return html``;
  }

  /**
   * Retrieves additional general information for the finding details.
   * @param findingDetails - The finding details payload.
   * @param dependencies - The dependencies.
   * @returns The additional general information template.
   */
  static getGeneralInfoBottom(findingDetails: FindingDetailsPayload, dependencies: any): TemplateResult {
    return html``;
  }

  /**
   * Retrieves the main category title for the related findings.
   * @param relatedFindings - The related findings group.
   * @returns The main category title template.
   */
  static getMainCategoryTitle(relatedFindings: FindingGroup): string {
    let groupTranslation = this.stringsTranslator.translateGroupedFindingCounter(
      relatedFindings.type,
      relatedFindings.count
    );
    if (relatedFindings.count === 1) {
      groupTranslation = `${groupTranslation}: ${relatedFindings.items[0].name}`;
    }
    return groupTranslation;
  }

  /**
   * Retrieves the subtitle for the related findings.
   * @param relatedFindings - The related findings group.
   * @returns The subtitle string.
   */
  static getSubtitle(relatedFindings: FindingGroup): string {
    return '';
  }

  /**
   * Retrieves the category title template for the related findings.
   * @param relatedFindings - The related findings group.
   * @returns The category title template.
   */
  static getCategoryTitleTemplate(relatedFindings: FindingGroup): TemplateResult {
    const subtitleString = this.getSubtitle(relatedFindings);
    if (subtitleString !== '') {
      return html` <div class="category-titles">
        <gz-tooltip tooltipText="${this.getMainCategoryTitle(relatedFindings)}" isEllipsis class="label mb-1" part="label">${this.getMainCategoryTitle(relatedFindings)}</gz-tooltip>
        <gz-tooltip tooltipText="${this.getSubtitle(relatedFindings)}" isEllipsis class="secondary-label" part="label">${this.getSubtitle(relatedFindings)}</gz-tooltip>
      </div>`;
    }
    return html` <gz-tooltip tooltipText="${this.getMainCategoryTitle(relatedFindings)}" isEllipsis class="label" part="label">${this.getMainCategoryTitle(relatedFindings)}</gz-tooltip>`;
  }

  /**
   * Retrieves the category information for the related findings.
   * @param relatedFindings - The related findings group.
   * @param classType - The finding class type.
   * @param onClickHandler - The click handler for the details.
   * @returns The category information template.
   */
  static getCategoryInfo(
    relatedFindings: FindingGroup,
    classType: FindingClassType,
    onClickHandler: OnClickDetailHandler
  ): TemplateResult {
    const iconName = findingTypeToIconMap[relatedFindings.type];

    return html` <gz-link
      type="quiet"
      color="quiet"
      @click="${() => onClickHandler(classType, relatedFindings.type)}"
      class="iconLabelBtn iconLabelBtn d-flex pb-1"
    >
      ${EASMIconSet.createIconSet(iconName)} ${this.getCategoryTitleTemplate(relatedFindings)}
    </gz-link>`;
  }

  /**
   * Retrieves the related information for a related finding.
   * @param relatedFinding - The related finding.
   * @returns The related information template or nothing.
   */
  static getRelatedInfo(relatedFinding: RelatedFinding): TemplateResult | typeof nothing {
    return nothing;
  }
}
